import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { languageOptions } from '../data/mock';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import UserMenu from './UserMenu';

const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/reviews', label: t('nav.reviews') },
    { path: '/series', label: t('nav.series') || 'TV Series' },
    { path: '/stars', label: t('nav.stars') || 'Stars' },
    { path: '/editorial', label: t('nav.editorial') },
    { path: '/categories', label: t('nav.categories') },
    { path: '/about', label: t('nav.about') },
  ];

  const currentLang = languageOptions.find(l => l.code === language);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        isScrolled ? 'bg-[#0B0F0E]/95 backdrop-blur-sm' : 'bg-[#0B0F0E]'
      }`}
      style={{ borderBottom: '1px solid rgba(0, 229, 168, 0.1)' }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group flex-shrink-0 mr-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_filmanalyzer/artifacts/ak5g8xbg_My%20Logo.png" 
              alt="Matrix Film Review Logo" 
              className="h-9 w-auto object-contain flex-shrink-0"
            />
            <span className="text-[#E6FFF6] font-semibold text-lg hidden sm:block group-hover:text-[#52ea88] transition-colors duration-300 whitespace-nowrap">
              Matrix Film Review
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-base transition-colors duration-300 whitespace-nowrap ${
                  location.pathname === link.path
                    ? 'text-[#52ea88]'
                    : 'text-[#9FBFB4] hover:text-[#E6FFF6]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Language Selector & Mobile Menu */}
          <div className="flex items-center gap-4">
            {/* Language Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 px-3 py-2 text-[#E6FFF6] hover:text-[#52ea88] transition-colors duration-300 outline-none">
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">{currentLang?.name}</span>
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#121917] border border-[#52ea88]/15 min-w-[160px]">
                {languageOptions.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#52ea88]/5 ${
                      language === lang.code ? 'text-[#52ea88]' : 'text-[#E6FFF6]'
                    }`}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span>{lang.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            <UserMenu />

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-[#E6FFF6] hover:text-[#52ea88] transition-colors"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="lg:hidden py-6 border-t border-[#52ea88]/10">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-lg py-2 transition-colors duration-300 ${
                    location.pathname === link.path
                      ? 'text-[#52ea88]'
                      : 'text-[#9FBFB4] hover:text-[#E6FFF6]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
